import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { OTHER_DOC_TYPES } from '../../../../mocks/cases';
import type { DocumentReviewStatus } from '../../../../mocks/cases';
import CategoryHeader from './CategoryHeader';
import type { DocState } from './types';
import { randomSize, reviewStatusBadgeCls, reviewStatusLabel } from './utils';
import { DOCUMENT_FILE_ACCEPT } from '../../../../utils/documentUploadPolicy';

interface Props {
  docs: DocState[];
  onDocsChange: (next: DocState[]) => void;
  categoryReview: DocumentReviewStatus;
  onCategoryReviewChange: (status: DocumentReviewStatus) => void;
  onViewDocument?: (doc: DocState) => void;
}

function supportingLabelForOtherType(value: string): string | undefined {
  const v = String(value || '').trim();
  if (!v || v === 'otros') return undefined;
  return OTHER_DOC_TYPES.find((t) => t.value === v)?.label ?? v;
}

export default function OtherCategory({
  docs,
  onDocsChange,
  categoryReview,
  onCategoryReviewChange,
  onViewDocument,
}: Props) {
  const { t } = useTranslation();
  const [newDocType, setNewDocType] = useState('');
  const [otrosDescription, setOtrosDescription] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const catDocs = docs.filter((d) => d.category === 'other' && d.uploaded);

  const otrosNeedsText = String(newDocType).toLowerCase() === 'otros';
  const canChooseFile =
    Boolean(newDocType) && (!otrosNeedsText || Boolean(otrosDescription.trim()));

  return (
    <div className="max-w-[480px]">
      <CategoryHeader
        title={t('advisorPortal.docCat_other_name')}
        category="other"
        status={categoryReview}
        onChange={onCategoryReviewChange}
      />

      {catDocs.length > 0 && (
        <div className="mb-3 space-y-1.5">
          {catDocs.map((doc) => (
            <div
              key={doc.id}
              className="bg-white rounded-xl border border-slate-100 px-4 py-3 flex flex-col gap-3 min-[420px]:flex-row min-[420px]:items-center"
            >
              <i className="ri-file-text-line text-slate-500 text-lg shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="min-w-0 text-[13px] font-medium text-slate-700 truncate">
                    {doc.uploadedFile}
                  </p>
                  <span
                    className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${reviewStatusBadgeCls(doc.reviewStatus)}`}
                  >
                    {reviewStatusLabel(doc.reviewStatus)}
                  </span>
                </div>
                <p className="text-[11px] text-slate-500">
                  {OTHER_DOC_TYPES.find((x) => x.value === doc.docType)?.label ?? doc.docType}
                  {doc.docType === 'otros' && doc.supportingDescription ? (
                    <span>: {doc.supportingDescription}</span>
                  ) : null}
                </p>
              </div>
              <button
                type="button"
                onClick={() => onViewDocument?.(doc)}
                className="min-h-[36px] px-3 rounded-lg border border-slate-200 text-[12px] font-medium text-slate-600 hover:bg-slate-50 cursor-pointer shrink-0"
              >
                View
              </button>
              <button
                type="button"
                onClick={() => onDocsChange(docs.filter((d) => d.id !== doc.id))}
                className="w-9 h-9 flex items-center justify-center text-slate-300 hover:text-slate-500 cursor-pointer shrink-0"
              >
                <i className="ri-close-line text-base" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div>
        <label className="block text-[13px] font-semibold text-slate-900 mb-1.5">
          {t('advisorPortal.documentTypeLabel', { defaultValue: 'Document type' })}
        </label>
        <select
          value={newDocType}
          onChange={(e) => {
            setNewDocType(e.target.value);
            setOtrosDescription('');
          }}
          className={`w-full min-h-[44px] px-3 border border-slate-200 rounded-xl text-sm bg-white mb-3 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400 ${!newDocType ? 'text-slate-400' : 'text-slate-700'}`}
        >
          <option value="">{t('advisorPortal.selectDocumentType', { defaultValue: 'Select…' })}</option>
          {OTHER_DOC_TYPES.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        {otrosNeedsText ? (
          <div className="mb-3">
            <label className="block text-[12px] font-medium text-slate-700 mb-1">
              {t('advisorPortal.otrosDescriptionLabel', { defaultValue: 'Describe the document' })}
            </label>
            <textarea
              value={otrosDescription}
              onChange={(e) => setOtrosDescription(e.target.value)}
              rows={2}
              maxLength={500}
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400 resize-y min-h-[56px]"
              placeholder={t('advisorPortal.otrosDescriptionPlaceholder', {
                defaultValue: 'Short description of this document',
              })}
            />
          </div>
        ) : null}

        <input
          ref={fileRef}
          type="file"
          accept={DOCUMENT_FILE_ACCEPT}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file || !newDocType) return;
            if (otrosNeedsText && !otrosDescription.trim()) return;

            const supportingDescription =
              otrosNeedsText
                ? otrosDescription.trim()
                : supportingLabelForOtherType(newDocType);

            onDocsChange([
              ...docs,
              {
                id: `oth-${Date.now()}`,
                name: file.name,
                category: 'other',
                docType: newDocType,
                uploaded: true,
                uploadedAt: new Date().toISOString().slice(0, 10),
                uploadedFile: file.name,
                rawFile: file,
                reviewStatus: 'pending' as DocumentReviewStatus,
                fileSize: randomSize(),
                supportingDescription,
              },
            ]);
            setNewDocType('');
            setOtrosDescription('');
            if (fileRef.current) fileRef.current.value = '';
          }}
          className="hidden"
        />

        <div className="flex">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={!canChooseFile}
            className="w-full min-h-[44px] bg-white border border-slate-200 rounded-lg flex items-center justify-center gap-2 text-sm text-slate-600 hover:border-slate-300 hover:bg-slate-50 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="ri-file-upload-line" />{' '}
            {t('advisorPortal.chooseFile', { defaultValue: 'Choose file' })}
          </button>
        </div>

        <p className="text-[11px] text-slate-500 mt-2 leading-snug">
          {t('advisorPortal.documentUploadRulesHint', {
            defaultValue:
              'PDF, DOC, JPG, TIF, or GIF. Max 6 MB per file. File name: letters, numbers, spaces, _ - . ( )',
          })}
        </p>
      </div>
    </div>
  );
}
